import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { EULAView } from "../../components/account/EULA";
import { CardLayout3 } from "../../components/account/register/card_layout_3";
import { getOAuthAuthorizeUrl, registerAuth, verifyAuth } from "../../controllers/accounts/auth";
import { updateSingleAccount } from "../../controllers/accounts/accounts";
import { ProfileQuesitons } from "../../components/account/ProfileQuestions/profile_questions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";

import { UserContext } from "../../context/UserContext";

function RegisterAccountProccess() {
  const [page, setPage] = useState(0);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useContext(UserContext);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalTitle, setStatusModalTitle] = useState("");
  const [statusModalMessage, setStatusModalMessage] = useState("");
  const [statusModalType, setStatusModalType] = useState("info");
  const [redirectToLoginOnClose, setRedirectToLoginOnClose] = useState(false);

  const showStatusModal = (title, message, shouldRedirectToLogin = false, type = "info") => {
    setStatusModalTitle(title);
    setStatusModalMessage(message);
    setRedirectToLoginOnClose(shouldRedirectToLogin);
    setStatusModalType(type);
    setStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    if (redirectToLoginOnClose) {
      window.location.href = getOAuthAuthorizeUrl();
    }
    setRedirectToLoginOnClose(false);
  };

  // Redirect if already logged in
  useEffect(() => {
    // Debug: log userDetails
    console.log('[RegisterAccountProccess] userDetails:', userDetails);
    const rawUser = localStorage.getItem('user');
    const rawToken = localStorage.getItem('token');
    const localUser = rawUser ? JSON.parse(rawUser) : null;
    const isObject = val => val && typeof val === 'object' && !Array.isArray(val);
    const hasKeys = val => isObject(val) && Object.keys(val).length > 0;
    const registrationPaths = ['/account/create', '/register'];
    const currentPath = location.pathname;
    const contextHasToken = typeof userDetails?.token === 'string' && userDetails.token.trim() !== '';
    const localHasToken = typeof rawToken === 'string' && rawToken.trim() !== '';
    const currentUserId = userDetails?.id || localUser?.id;
    const isAuthenticated = (contextHasToken || localHasToken) && Boolean(currentUserId);

    // Only redirect if on a registration page and not already on /profile
    if (
      isAuthenticated &&
      (hasKeys(userDetails) || hasKeys(localUser)) &&
      registrationPaths.includes(currentPath) &&
      currentPath !== '/profile'
    ) {
      navigate(`/profile/${currentUserId}`);
    }
  }, [userDetails, navigate, location.pathname]);

  // Verify email is confirmed
  const mutation_confirm_email = useMutation({
    mutationFn: verifyAuth,
    onSuccess: (data) => {
      console.log(data);
      if (data?.error) {
        showStatusModal("Email confirmation failed", `${data?.error}`, false, "error");
      } else {
        // Mastodon rejects account updates until the email is confirmed, so the
        // discoverable flag must be set here rather than right after registration.
        updateSingleAccount({ body: { discoverable: true }, token }).catch(() => {});
        showStatusModal(
          "Email confirmed",
          "Email confirmed successfully. Please login to continue.",
          true,
          "success"
        );
      }
      // queryClient.invalidateQueries({ queryKey: ["registerAuth"] });
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "Unknown error during email confirmation.";
      showStatusModal("Email confirmation failed", errorMessage, false, "error");
    },
  });

  const sendVerifyEmail = (token) => {
    mutation_confirm_email.mutate( token );
  };

  // Mastodon registration
  const [bio_value, set_bio_value] = useState("Tell us about yourself.");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPass, setconfirmPassword] = useState("");
  const [agreement, setAgreement] = useState(true);
  const [verifyEmail, setVerifyEmail] = useState(false);
  const [token, setToken] = useState("");

  const regKeys = [
    { label: "Username", value: username, setValue: setUsername },
    { label: "Email", value: email, setValue: setEmail },
    { label: "Password", value: password, setValue: setPassword },
    { label: "Confirm Password", value: confirmPass, setValue: setconfirmPassword },
  ];

  const card3_fields = [
    {
      register_page: true,
      page: page,
      setPage: setPage,
      open: true,
      setOpen: () => {},
      btn_label: "Bio",
      lastpage: false,
      content: (
        <TextField
          placeholder={bio_value}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon />
              </InputAdornment>
            ),
          }}
          onChange={(e) => set_bio_value(e.target.value)}
          multiline
          fullWidth
        />
      ),
      page_number: 1,
    },
    {
      register_page: true,
      page: page,
      setPage: setPage,
      open: true,
      setOpen: () => {},
      btn_label: "Account Details",
      lastpage: true,
      content: !verifyEmail ? (
        <React.Fragment>
          {regKeys.map((reg, i) => (
            <TextField
              key={i}
              label={reg.label}
              type={reg.label.includes("Password") ? "password" : "text"}
              onChange={(e) => reg.setValue(e.target.value)}
              value={reg.value}
              fullWidth
              margin="normal"
            />
          ))}
          <Button variant="contained" onClick={() => sendMastodonRegistration()}>
            NEXT
          </Button>
        </React.Fragment>
      ) : (
        <Box
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 3,
            p: 3,
            mt: 1,
            textAlign: "center",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.2 }}>
            Almost done
          </Typography>
          <Typography variant="h6" sx={{ mt: 0.5, mb: 1, fontWeight: 700 }}>
            Verify your email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We sent a confirmation email. Open it, confirm your account, then continue below.
          </Typography>

          <CircularProgress size={30} sx={{ my: 2 }} />

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Tip: check your spam or promotions folder if you can’t find it.
          </Typography>

          <Button variant="contained" size="large" fullWidth onClick={() => sendVerifyEmail(token)}>
            I confirmed my email
          </Button>
        </Box>
      ),
      page_number: 2,
    },
  ];

  // Register a Mastodon account
  const mutation = useMutation({
    mutationFn: registerAuth,
    onSuccess: (data) => {
      console.log(data);
      if (data?.error) {
        console.log(data);
        showStatusModal("Registration failed", data?.error || "Registration errors", false, "error");
      } else {
        setToken(data.access_token);
        setVerifyEmail(true);
      }
      // queryClient.invalidateQueries({ queryKey: ["registerAuth"] });
    },
  });

  const sendMastodonRegistration = () => {
    if (!username || !email || !password || !agreement) {
      showStatusModal("Missing information", "All fields must be filled out.");
      return;
    }
    mutation.mutate({
      register_data: {
        username,
        email,
        password,
        agreement,
        note: bio_value,
        locale: "en",
      },
    });
  };

  return (
    <Box sx={{ width: "95%", padding: 2 }}>
      <Slider
        value={page}
        max={18}
        min={0}
        step={1}
        disabled
        marks
        sx={{ height: 5 }}
      />
      {page === 0 && <EULAView page={page} setPage={setPage} />}
      {card3_fields.map(
        (item, i) =>
          page === item.page_number && (
            <CardLayout3
              key={i}
              register_page={item.register_page}
              page={item.page}
              setPage={item.setPage}
              open={item.open}
              setOpen={item.setOpen}
              btn_label={item.btn_label}
              lastpage={item.lastpage}
              content={item.content}
            />
          )
      )}
      <ProfileQuesitons page={page} setPage={setPage} register_page={true} token={token} />

      <Dialog open={statusModalOpen} onClose={handleCloseStatusModal} fullWidth maxWidth="xs">
        <DialogTitle
          sx={{
            bgcolor:
              statusModalType === "success"
                ? "success.main"
                : statusModalType === "error"
                ? "error.main"
                : "primary.main",
            color: "common.white",
          }}
        >
          {statusModalTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">{statusModalMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseStatusModal}
            variant="contained"
            color={statusModalType === "success" ? "success" : statusModalType === "error" ? "error" : "primary"}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export { RegisterAccountProccess };
export default RegisterAccountProccess;
